
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { TriangleAlert } from 'lucide-react';
import Sidebar from './sidebar';
import AllerAlAncre from './AllerAlAncre';
import PageIntrouvable from './PageIntrouvable';
import { useDroits } from '../context/Droits';
import Header from './header';
import { INCIDENT } from '../services/api.jsx';
import "../styles/admin-global.css";

/* ── Le bandeau d'incident ────────────────────────────────────────────
   Il remplace la pastille « services opérationnels », qui occupait de la
   place en permanence pour annoncer ce qui est vrai presque tout le
   temps. Ici, rien ne s'affiche tant que tout va bien ; dès que le
   serveur ne répond plus, une phrase le dit — sans code d'erreur, que
   personne n'a à connaître.

   `role="alert"` : les lecteurs d'écran l'annoncent sans attendre que la
   personne arrive dessus en naviguant. */
function BandeauIncident() {
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const ecouter = (e) => setMessage(e.detail?.message || null);
    window.addEventListener(INCIDENT, ecouter);
    return () => window.removeEventListener(INCIDENT, ecouter);
  }, []);

  if (!message) return null;

  return (
    <div role="alert"
         className="encart encart-alerte mb-2.5">
      <TriangleAlert className="w-[18px] h-[18px] shrink-0 mt-[1px]" strokeWidth={2} />
      <p className="m-0 text-[13.5px] font-medium">{message}</p>
    </div>
  );
}

export default function AdminLayout({ user, theme, onSetTheme, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 1024);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const containerRef = useRef(null);

  /* Le garde de route.
     Une adresse dont la zone n'est pas ouverte au compte n'affiche pas
     la page : elle affiche « introuvable », comme le serveur qui répond
     404. Tant que la réponse n'est pas arrivée, on ne tranche pas —
     afficher « introuvable » une fraction de seconde avant de se
     raviser serait pire que d'attendre. */
  const { pretes, zoneDuChemin, zones } = useDroits();
  const zoneCourante = zoneDuChemin(location.pathname);
  const cheminsConnus = zones.map((z) => z.chemin);
  // Une page sans zone déclarée — profil, paramètres, aide — est ouverte
  // à toute l'équipe : il n'y a rien à habiliter.
  const horsZones = ['/profile', '/settings', '/help', '/'];
  const fermee = pretes
    && !zoneCourante
    && !horsZones.some((c) => location.pathname === c || location.pathname.startsWith(`${c}/`))
    && cheminsConnus.length > 0;

  const activePage = useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    if (segments.length === 0) return 'dashboard';
    return segments[0];
  }, [location.pathname]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const root = document.documentElement;
    
    const themes = {
      light: {
        // Les valeurs du modele v3. #EDF3EF laissait « texte doux » a 4,45
        // de contraste, sous le seuil ; #F3F6F4 le porte a 4,60.
        '--bg-main': '#F3F6F4',
        // Le fond HORS du cadre blanc, plus soutenu : c'est lui qui détache
        // le cadre. Le modèle les distingue, les confondre l'aplatit.
        '--fond-app': '#E3EAE5',
        '--bg-side': '#F3F6F4',
        '--bg-header': 'rgba(249, 252, 250, 0.86)',
        '--bg-card': '#FFFFFF',
        '--border': '#D5DFD9',
        '--text-bold': '#102538',
        '--text-main': '#2E465D',
        '--text-muted': '#5A7288',
        '--accent': '#5EBD4F'
      },
      // Mode sombre concu pour de longues sessions, pas pour l'effet.
      //
      // Trois regles tenues ici :
      //   - pas de blanc pur sur fond sombre. #F2FFF7 sur presque noir produit
      //     un halo et fatigue au bout de quelques minutes ; #E4EAE6 se lit
      //     aussi bien sans eblouir (13:1, largement au-dessus du minimum) ;
      //   - un fond peu sature. L'ancien #11201E tirait fortement vers le vert
      //     et teintait tout ce qui se posait dessus ;
      //   - des surfaces opaques plutot que translucides. `rgba(255,255,255,.07)`
      //     s'empilait : deux cartes superposees donnaient un gris different
      //     d'une carte seule.
      dark: {
        '--bg-main': '#12181A',
        '--fond-app': '#0B0F10',
        '--bg-side': '#0E1416',
        '--bg-header': 'rgba(14, 20, 22, 0.90)',
        '--bg-card': '#1A2224',
        '--border': '#2A3437',
        '--text-bold': '#E4EAE6',
        '--text-main': '#C2CCC7',
        '--text-muted': '#8A9691',
        '--accent': '#6FC95F'
      },
    };

    // `forest` est l'ancien nom du mode sombre : on le fait suivre pour ne
    // pas renvoyer en clair quelqu'un dont la preference etait deja posee.
    const demande = theme === 'forest' ? 'dark' : theme;

    // « Systeme » n'est pas un troisieme jeu de couleurs : c'est clair ou
    // sombre, decide par l'appareil. On ecoute donc la preference du
    // navigateur, sans quoi le choix ne vaudrait qu'au chargement de la page
    // et ne suivrait pas le basculement automatique du soir.
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    let posees = [];

    const appliquer = () => {
      const resolu = demande === 'system'
        ? (media.matches ? 'dark' : 'light')
        : demande;
      const actif = themes[resolu] || themes.light;
      posees = Object.keys(actif);
      Object.entries(actif).forEach(([key, val]) => {
        container.style.setProperty(key, val);
        root.style.setProperty(key, val);
      });
    };

    appliquer();
    if (demande === 'system') {
      media.addEventListener('change', appliquer);
    }

    return () => {
      media.removeEventListener('change', appliquer);
      posees.forEach(key => {
        container.style.removeProperty(key);
        root.style.removeProperty(key);
      });
    };
  }, [theme]);

  /* On n'agit qu'au FRANCHISSEMENT du seuil, jamais à chaque redimensionnement.
     La version précédente rouvrait la barre à tout événement `resize` dès lors
     qu'on était sur grand écran : l'état réduit ne survivait donc pas au
     premier redimensionnement de fenêtre — ni, sur mobile, à l'apparition du
     clavier virtuel, qui en déclenche un. Un réglage que l'interface annule
     d'elle-même vaut moins que pas de réglage du tout. */
  useEffect(() => {
    let etaitMobile = window.innerWidth < 1024;
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      if (mobile === etaitMobile) return;
      etaitMobile = mobile;
      setIsMobile(mobile);
      // En passant au tiroir on le referme ; en revenant au grand écran on
      // rouvre, car 68 px sans l'avoir demandé ressemble à un défaut.
      setSidebarOpen(!mobile);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => setSidebarOpen(!isSidebarOpen);

  const handleNavigation = (page) => {
    navigate(`/${page}`);
  };

  const handleNavWithClose = (page) => {
    handleNavigation(page);
    if (isMobile) setSidebarOpen(false);
  };

  return (
    /* ── La coque du modèle « Admin ASSO-PRO v3 » ────────────────────────
       Un cadre blanc arrondi flotte sur le fond d'application, et contient
       deux cartes côte à côte : la barre latérale et le panneau de contenu.
       Avant, la barre était fixée au bord de l'écran et le contenu décalé
       d'une marge — d'où deux largeurs à tenir accordées dans deux
       fichiers. Ici c'est une rangée flex : plus rien à synchroniser. */
    <div
      ref={containerRef}
      className="admin-scope h-full p-2 sm:p-3 transition-colors duration-500"
      style={{ backgroundColor: 'var(--fond-app, var(--bg-main))' }}
    >
      {/* Le voile du tiroir, sur mobile seulement. */}
      {isMobile && isSidebarOpen && (
        <div
          className="fixed inset-0 bg-[#0B1620]/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Le cadre a une hauteur DEFINIE, pas un minimum.
          Avec `min-height`, la colonne pouvait grandir sans limite : un
          enfant en `flex-1` n'avait alors rien a se partager, et la
          messagerie restait a 557 px dans une fenetre de 860. Une
          hauteur definie fait defiler le contenu DANS le panneau --
          l'en-tete et la barre laterale restent en place, ce qui est la
          structure du modele. */}
      <div
        className="flex items-stretch gap-2 sm:gap-2.5 rounded-cadre bg-[var(--bg-card)]
                   p-2 sm:p-2.5 shadow-cadre
                   h-full"
      >

      <Sidebar
        isOpen={isSidebarOpen}
        activePage={activePage}
        setPage={handleNavWithClose}
        user={user}
        role={user?.role}
        isMobile={isMobile}
        onClose={() => setSidebarOpen(false)}
      />

      {/* La colonne de contenu. `min-w-0` est indispensable : sans lui, un
          tableau large pousse la colonne flex et fait déborder toute la
          page horizontalement — le défaut mesuré sur le modèle v3.

          Le modèle n'y met pas UN panneau mais DEUX cartes empilées, la
          barre du haut puis la page, séparées par 10 px de fond. C'est ce
          vide qui remplace le filet inférieur de l'ancien bandeau. */}
      <div className="flex-1 min-w-0 self-stretch flex flex-col gap-2.5">
        <Header
          user={user} 
          isSidebarOpen={isSidebarOpen} 
          toggleSidebar={toggleSidebar}
          theme={theme}
          onSetTheme={onSetTheme}
          onLogout={onLogout}
          onProfileClick={() => { handleNavigation('settings'); if (isMobile) setSidebarOpen(false); }}
        />
        
        {/* La carte de la page. Les marges internes sont celles du modèle
            (28 px en haut, 26 de côté, 34 en bas), resserrées sur mobile. */}
        {/* Colonne flex : une page qui doit remplir la hauteur — la
            messagerie — le demande par `flex-1 min-h-0` au lieu de la
            deviner avec un `calc(100vh - 19rem)` qui se trompe des que
            l'en-tete change d'un pixel. Les autres pages s'empilent
            exactement comme avant. */}
        <main className="flex-1 min-h-0 flex flex-col overflow-y-auto rounded-carte
                         bg-[var(--bg-main)]
                         px-4 pt-5 pb-8 sm:px-[26px] sm:pt-7 sm:pb-[34px]">
          <AllerAlAncre />
          <BandeauIncident />
          {fermee ? <PageIntrouvable /> : <Outlet />}
        </main>
      </div>

      </div>
    </div>
  );
}
